// Spend, for two audiences in one page (IMPLEMENTATION_PLAN.md §18.3).
//
// Everyone sees their own spend. An admin can additionally switch to the org-wide view, and that
// is the only thing the role grants here: no board titles, no prompts, no images. Administering
// the system is not a reason to read the contents of someone's work (§17), so the view that could
// have shown it simply does not exist.
//
// Every figure is an estimate from published prices (§18.1) — the word "Estimated" in the stat
// labels carries that; the page does not explain itself beyond it.

import { useEffect, useState } from "react";
import type { ReactElement, ReactNode } from "react";
import { Link } from "react-router-dom";
import { api, formatMicroUsd, formatRelative } from "../api";
import type {
  BoardSpend,
  DailySpend,
  ModelSpend,
  OrgSpend,
  SpendSummary,
  UserSpend,
} from "../api";
import { EmptyState, ErrorNote, Skeleton, describeError } from "../components/ui";

type Scope = "you" | "everyone";

const WINDOWS = [7, 30, 90];

/** The loaded payload carries the scope it came from, so a stale frame is never relabelled. */
type Loaded =
  | { scope: "you"; summary: SpendSummary }
  | { scope: "everyone"; summary: OrgSpend };

export function AnalyticsPage(): ReactElement {
  const [isAdmin, setIsAdmin] = useState(false);
  const [scope, setScope] = useState<Scope>("you");
  const [days, setDays] = useState(30);

  const [data, setData] = useState<Loaded | null>(null);
  const [pending, setPending] = useState(true);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    const controller = new AbortController();
    api
      .session(controller.signal)
      .then((session) => setIsAdmin(session.user?.is_admin ?? false))
      .catch(() => {
        // Not knowing the role only costs the "Everyone" segment. Own spend still loads.
      });
    return () => controller.abort();
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    setPending(true);
    setError(null);

    const request: Promise<Loaded> =
      scope === "everyone"
        ? api
            .orgSpend(days, controller.signal)
            .then((summary) => ({ scope: "everyone", summary }) satisfies Loaded)
        : api
            .mySpend(days, controller.signal)
            .then((summary) => ({ scope: "you", summary }) satisfies Loaded);

    request
      .then((loaded) => {
        setData(loaded);
        setPending(false);
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        setError(cause);
        setPending(false);
      });

    return () => controller.abort();
  }, [scope, days]);

  const failure = error ? describeError(error) : null;
  // A window change keeps the previous frame at reduced opacity; a scope change does not, because
  // holding one person's numbers under the other view's heading would misread.
  const frame = data !== null && data.scope === scope ? data : null;

  return (
    <div className="page">
      <div className="page-inner">
        <div className="page-header">
          <div>
            <h1 className="page-title">Analytics</h1>
            <p className="lede">Estimated spend, by day, model and board.</p>
          </div>
        </div>

        <div className="toolbar">
          {isAdmin && (
            <div className="segmented" role="group" aria-label="Whose spend">
              <SegmentButton
                selected={scope === "you"}
                label="You"
                onClick={() => setScope("you")}
              />
              <SegmentButton
                selected={scope === "everyone"}
                label="Everyone"
                onClick={() => setScope("everyone")}
              />
            </div>
          )}

          <div className="segmented" role="group" aria-label="Time window">
            {WINDOWS.map((option) => (
              <SegmentButton
                key={option}
                selected={days === option}
                label={`${option} days`}
                onClick={() => setDays(option)}
              />
            ))}
          </div>
        </div>

        {failure && (
          <div style={{ marginBottom: "var(--space-5)" }}>
            <ErrorNote title={failure.title} detail={failure.detail} />
          </div>
        )}

        {frame === null && !failure && <LoadingFrame />}

        {frame !== null && (
          <div
            // Refetching holds the rendered frame rather than flashing skeletons back in.
            style={{ opacity: pending ? 0.55 : 1, transition: "opacity var(--fast) var(--ease)" }}
          >
            {frame.scope === "you" ? (
              <OwnSpend summary={frame.summary} />
            ) : (
              <OrgWideSpend summary={frame.summary} />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── The two views ──────────────────────────────────────────────────────────────────────────

function OwnSpend({ summary }: { summary: SpendSummary }): ReactElement {
  if (summary.generation_count === 0) {
    return (
      <EmptyState
        title="You have not generated anything yet."
        hint="Make a frame in the studio and its estimated cost appears here."
        action={
          <Link className="btn primary" to="/">
            Open the studio
          </Link>
        }
      />
    );
  }

  return (
    <>
      <div className="stat-grid">
        <Stat
          label={`Estimated spend, last ${summary.window_days} days`}
          value={formatMicroUsd(summary.window_micro_usd)}
        />
        <Stat label="Estimated spend, all time" value={formatMicroUsd(summary.total_micro_usd)} />
        <Stat
          label={`Generations, last ${summary.window_days} days`}
          value={summary.window_generation_count.toLocaleString()}
          note={`${summary.generation_count.toLocaleString()} all time`}
        />
        <Stat label="Failed generations" value={summary.failed_count.toLocaleString()} />
      </div>

      <div className="col" style={{ gap: "var(--space-6)" }}>
        <Section title={`Spend by day, last ${summary.window_days} days`}>
          <DailySpendChart daily={summary.daily} subject="Your estimated spend" />
        </Section>

        <Section title="By model">
          <ModelTable rows={summary.by_model} />
        </Section>

        <Section title="By board">
          <BoardTable rows={summary.by_board} />
        </Section>
      </div>
    </>
  );
}

function OrgWideSpend({ summary }: { summary: OrgSpend }): ReactElement {
  if (summary.generation_count === 0) {
    return (
      <EmptyState
        title="Nobody has generated anything yet."
        hint="Add people on the People page, or make the first frame yourself."
        action={
          <Link className="btn primary" to="/">
            Open the studio
          </Link>
        }
      />
    );
  }

  return (
    <>
      <div className="stat-grid">
        <Stat
          label={`Estimated spend, last ${summary.window_days} days`}
          value={formatMicroUsd(summary.window_micro_usd)}
        />
        <Stat label="Estimated spend, all time" value={formatMicroUsd(summary.total_micro_usd)} />
        <Stat
          label={`Generations, last ${summary.window_days} days`}
          value={summary.window_generation_count.toLocaleString()}
        />
        <Stat
          label="Generations, all time"
          value={summary.generation_count.toLocaleString()}
          note={`${summary.by_user.length.toLocaleString()} people`}
        />
      </div>

      <div className="col" style={{ gap: "var(--space-6)" }}>
        <Section title={`Spend by day, last ${summary.window_days} days`}>
          <DailySpendChart daily={summary.daily} subject="Estimated spend for everyone" />
        </Section>

        <Section title="By person">
          <PersonTable rows={summary.by_user} windowDays={summary.window_days} />
        </Section>

        <Section title="By model">
          <ModelTable rows={summary.by_model} />
        </Section>
      </div>
    </>
  );
}

// ── The chart ──────────────────────────────────────────────────────────────────────────────

const PLOT_HEIGHT = 132;

/**
 * A single-series column chart of divs, in the accent colour, with the same numbers in a table
 * underneath. Columns rather than a line because the data is discrete daily buckets, many of them
 * zero, and a line drawn through a zero day implies spend that did not happen.
 *
 * The server returns a continuous date spine, so the columns are the days — nothing is synthesised
 * or interpolated here.
 */
function DailySpendChart({ daily, subject }: { daily: DailySpend[]; subject: string }): ReactElement {
  if (daily.length === 0) {
    return <p className="stat-note">No days in this window.</p>;
  }

  let total = 0;
  let peak = daily[0];
  for (const day of daily) {
    total += day.micro_usd;
    if (day.micro_usd > peak.micro_usd) peak = day;
  }

  const first = daily[0];
  const last = daily[daily.length - 1];
  const middle = daily[Math.floor((daily.length - 1) / 2)];

  // 90 columns cannot each carry a 2px gap on a 320px screen, so the gap narrows with the window
  // rather than the columns disappearing.
  const gap = daily.length > 45 ? 1 : 2;

  const label =
    peak.micro_usd === 0
      ? `${subject} by day, ${shortDay(first.date)} to ${shortDay(last.date)}. No spend on any day in this window.`
      : `${subject} by day, ${shortDay(first.date)} to ${shortDay(last.date)}. Total ${formatMicroUsd(total)} across ${daily.length} days, highest on ${shortDay(peak.date)} at ${formatMicroUsd(peak.micro_usd)}.`;

  return (
    <figure className="card">
      <div
        role="img"
        aria-label={label}
        style={{
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          gap,
          height: PLOT_HEIGHT,
          borderBottom: "1px solid var(--separator-soft)",
        }}
      >
        {daily.map((day) => (
          <div
            key={day.date}
            // The whole column is the hit target, so a zero day still answers on hover.
            title={`${shortDay(day.date)} · ${formatMicroUsd(day.micro_usd)} · ${day.generation_count} generations`}
            style={{
              flex: "1 1 0",
              minWidth: 0,
              maxWidth: 24,
              height: "100%",
              display: "flex",
              alignItems: "flex-end",
            }}
          >
            <div
              aria-hidden
              style={{
                width: "100%",
                // A day with spend is never invisible; a day without spend is never a sliver.
                height: barHeight(day.micro_usd, peak.micro_usd),
                background: "var(--accent)",
                borderRadius: "3px 3px 0 0",
              }}
            />
          </div>
        ))}
      </div>

      <div className="row" style={{ justifyContent: "space-between", marginTop: "var(--space-2)" }}>
        <span className="stat-note">{shortDay(first.date)}</span>
        {/* Three labels, not ninety. The middle one is dropped when it would repeat an end. */}
        {daily.length > 4 && <span className="stat-note">{shortDay(middle.date)}</span>}
        <span className="stat-note">{shortDay(last.date)}</span>
      </div>

      <figcaption className="stat-note" style={{ marginTop: "var(--space-2)" }}>
        {peak.micro_usd === 0
          ? "No spend in this window."
          : `Highest day: ${shortDay(peak.date)}, ${formatMicroUsd(peak.micro_usd)}.`}
      </figcaption>

      <details style={{ marginTop: "var(--space-3)" }}>
        <summary className="stamp" style={{ cursor: "pointer" }}>
          Show the daily figures
        </summary>
        <div className="table-scroll" style={{ marginTop: "var(--space-2)", maxHeight: 320, overflowY: "auto" }}>
          <table className="table">
            <thead>
              <tr>
                <th scope="col">Day</th>
                <th scope="col" className="num">
                  Generations
                </th>
                <th scope="col" className="num">
                  Spend
                </th>
              </tr>
            </thead>
            <tbody>
              {daily.map((day) => (
                <tr key={day.date}>
                  <td>{shortDay(day.date)}</td>
                  <td className="num">{day.generation_count.toLocaleString()}</td>
                  <td className="num">{formatMicroUsd(day.micro_usd)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </figure>
  );
}

/** Never divides by zero: an all-zero window draws a flat baseline, not an empty box. */
function barHeight(microUsd: number, peakMicroUsd: number): number {
  if (microUsd <= 0 || peakMicroUsd <= 0) return 0;
  return Math.max(2, Math.round((microUsd / peakMicroUsd) * PLOT_HEIGHT));
}

/** The buckets are UTC days (§18.3), so they are read back as UTC days rather than shifted local ones. */
function shortDay(isoDate: string): string {
  return new Date(`${isoDate}T00:00:00Z`).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  });
}

// ── Tables ─────────────────────────────────────────────────────────────────────────────────

function ModelTable({ rows }: { rows: ModelSpend[] }): ReactElement {
  if (rows.length === 0) {
    return (
      <EmptyState
        title="No priced generations in this window."
        hint="Pick a longer window, or generate a frame in the studio."
      />
    );
  }

  return (
    <div className="table-scroll">
      <table className="table">
        <thead>
          <tr>
            <th scope="col">Model</th>
            <th scope="col">Provider</th>
            <th scope="col" className="num">
              Generations
            </th>
            <th scope="col" className="num">
              Spend
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.model_id}>
              <td>{row.model_display_name}</td>
              <td className="muted">{row.provider_id}</td>
              <td className="num">{row.generation_count.toLocaleString()}</td>
              <td className="num">{formatMicroUsd(row.micro_usd)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function BoardTable({ rows }: { rows: BoardSpend[] }): ReactElement {
  if (rows.length === 0) {
    return (
      <EmptyState
        title="No boards with spend in this window."
        hint="Pick a longer window, or start a board in the studio."
      />
    );
  }

  return (
    <div className="table-scroll">
      <table className="table">
        <thead>
          <tr>
            <th scope="col">Board</th>
            <th scope="col" className="num">
              Generations
            </th>
            <th scope="col" className="num">
              Spend
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.board_id ?? "deleted"}>
              <td>
                {/* Spend from deleted boards keeps its one aggregate row; there is nothing to
                    link to any more. */}
                {row.board_id ? <Link to={`/b/${row.board_id}`}>{row.title}</Link> : row.title}
              </td>
              <td className="num">{row.generation_count.toLocaleString()}</td>
              <td className="num">{formatMicroUsd(row.micro_usd)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PersonTable({ rows, windowDays }: { rows: UserSpend[]; windowDays: number }): ReactElement {
  if (rows.length === 0) {
    return (
      <EmptyState
        title="Nobody has spent anything in this window."
        hint="Pick a longer window, or add people on the People page."
      />
    );
  }

  return (
    <div className="table-scroll">
      <table className="table">
        <thead>
          <tr>
            <th scope="col">Person</th>
            <th scope="col">Role</th>
            <th scope="col">Status</th>
            <th scope="col" className="num">
              Generations
            </th>
            <th scope="col" className="num">
              Last {windowDays} days
            </th>
            <th scope="col" className="num">
              All time
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.user_id}>
              <td>
                <span>{row.display_name || row.email}</span>
                <span className="stat-note" style={{ display: "block" }}>
                  {row.email} · seen {formatRelative(row.last_seen_at)}
                </span>
              </td>
              <td>{row.role === "admin" ? <span className="chip">Admin</span> : "User"}</td>
              <td>
                {row.is_active ? (
                  <span className="muted">Active</span>
                ) : (
                  <span className="chip warn">Deactivated</span>
                )}
              </td>
              <td className="num">{row.generation_count.toLocaleString()}</td>
              <td className="num">{formatMicroUsd(row.window_micro_usd)}</td>
              <td className="num">{formatMicroUsd(row.micro_usd)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Small parts ────────────────────────────────────────────────────────────────────────────

function SegmentButton({
  selected,
  label,
  onClick,
}: {
  selected: boolean;
  label: string;
  onClick: () => void;
}): ReactElement {
  return (
    <button
      type="button"
      className={selected ? "is-selected" : undefined}
      aria-pressed={selected}
      onClick={onClick}
    >
      {label}
    </button>
  );
}

function Stat({ label, value, note }: { label: string; value: string; note?: string }): ReactElement {
  return (
    <div className="stat">
      <span className="stat-value">{value}</span>
      <span className="stat-label">{label}</span>
      {note && <span className="stat-note">{note}</span>}
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }): ReactElement {
  return (
    <section className="col">
      <h2 className="section-title">{title}</h2>
      {children}
    </section>
  );
}

function LoadingFrame(): ReactElement {
  return (
    <>
      <div className="stat-grid">
        {[0, 1, 2, 3].map((index) => (
          <div className="stat" key={index}>
            <Skeleton height={28} width="60%" />
            <Skeleton height={12} width="80%" />
          </div>
        ))}
      </div>
      <div className="card">
        <Skeleton height={PLOT_HEIGHT} />
      </div>
    </>
  );
}
